import Icons, { IconData } from '@/icons';
import { BaseOrganization } from '.';

export enum BasePacketKind {
  SINGLE_POINT = 'SINGLE_POINT',
  MULTI_POINT = 'MULTI_POINT'
}

export const basePacketKindFromString = (s: string): BasePacketKind => {
  if (s === BasePacketKind.SINGLE_POINT) {
    return BasePacketKind.SINGLE_POINT;
  }

  return BasePacketKind.MULTI_POINT;
};

export interface Packet {
  kind: BasePacketKind;
  packetId: number;
  name: string;
  description: string;
  external: boolean;
  public: boolean;
  canEdit: boolean;
  objectives: Objective[];
  outcomes: Outcome[];
  usage: number;
}

export interface SinglePointPacket extends Packet {
  kind: BasePacketKind.SINGLE_POINT;
}

export interface MultiPointPacket extends Packet {
  kind: BasePacketKind.MULTI_POINT;
}

export type BasePacket = SinglePointPacket | MultiPointPacket;

export interface Objective {
  objectiveId: number;
  name: string;
  description: string;
  isActive: boolean;
  iconData: IconData;
  useOutcomeIcon: boolean;
}

export interface Outcome {
  outcomeId: number;
  value: string;
  isActive: boolean;
  iconData: IconData;
  color: string;
  sortOrder: number;
}

export interface Usage {
  userCount: number;
  postCount: number;
}

const convertIconData = (iconData: IconData) => {
  return {
    iconCodePoint: iconData.codePoint,
    iconFontFamily: iconData.apiFamily
  };
};

export const utils = {
  getPacketGoalsOverview(packet: BasePacket) {
    if (packet.kind === BasePacketKind.SINGLE_POINT) {
      return packet.outcomes.map(out => ({
        icon: out.iconData,
        name: out.value,
        color: out.color
      }));
    }

    return packet.objectives.map(obj => ({
      icon: obj.iconData,
      name: obj.name
    }));
  },

  /// Return the _unique_ set of icons included in this packet
  getPacketIcons(packet: BasePacket) {
    return packet.objectives
      .flatMap(o =>
        packet.kind === BasePacketKind.SINGLE_POINT
          ? packet.outcomes.map(oc => oc.iconData)
          : [o.iconData]
      )
      .reduce((acc: IconData[], iconData) => {
        return acc.some(
          existing =>
            existing.codePoint === iconData.codePoint &&
            existing.family === iconData.family
        )
          ? acc
          : [...acc, iconData];
      }, []);
  },

  convertApiPacket(
    apiPacket: ApiPacket,
    isExternalFn: (p: ApiPacket) => boolean
  ): BasePacket {
    const isSinglePointRubric = apiPacket.kind === 'single_point_rubric';

    const kind = isSinglePointRubric
      ? BasePacketKind.SINGLE_POINT
      : BasePacketKind.MULTI_POINT;

    const objectives = apiPacket.objectives.map(this.convertApiObjective);

    const outcomes = apiPacket.outcomeSpace.definedBy.map(
      this.convertApiOutcome
    );

    return {
      kind: kind,
      packetId: apiPacket.packetId,
      name: apiPacket.name,
      description: apiPacket.description,
      external: isExternalFn(apiPacket),
      objectives: objectives,
      outcomes: outcomes,
      public: apiPacket.public,
      canEdit: apiPacket.canEdit || false,
      usage: apiPacket.usage?.org || apiPacket.usage?.orgs || 0
    };
  },

  convertApiOutcome: (o: ApiOutcome): Outcome => ({
    outcomeId: o.outcomeId,
    value: o.value,
    isActive: o.isActive,
    color: o.color,
    iconData: Icons.lookup(o.iconFontFamily, o.iconCodePoint),
    sortOrder: o.sortOrder
  }),

  convertApiObjective: (o: ApiObjective): Objective => ({
    objectiveId: o.objectiveId,
    name: o.name,
    description: o.description,
    isActive: o.isActive,
    iconData: Icons.lookup(o.iconFontFamily, o.iconCodePoint),
    useOutcomeIcon: o.useOutcomeIcon
  }),

  convertObjective: ({
    objectiveId,
    name,
    description,
    useOutcomeIcon,
    iconData,
    isActive
  }: Objective): ApiObjective => ({
    objectiveId,
    name,
    description,
    isActive,
    useOutcomeIcon,
    ...convertIconData(iconData)
  }),

  convertPartialObjective: (o: Partial<Objective>): Partial<ApiObjective> => ({
    objectiveId: o.objectiveId,
    name: o.name,
    description: o.description,
    isActive: o.isActive,
    iconFontFamily: o.iconData?.apiFamily,
    iconCodePoint: o.iconData?.codePoint
  }),

  convertOutcome: ({
    outcomeId,
    value,
    isActive,
    iconData,
    color,
    sortOrder
  }: Outcome): ApiOutcome => ({
    outcomeId,
    value,
    isActive,
    color,
    sortOrder,
    ...convertIconData(iconData)
  }),

  convertPartialOutcome: (o: Partial<Outcome>): Partial<ApiOutcome> => ({
    outcomeId: o.outcomeId,
    value: o.value,
    isActive: o.isActive,
    iconFontFamily: o.iconData?.apiFamily,
    iconCodePoint: o.iconData?.codePoint,
    color: o.color
  }),

  convertApiBadge(b: ApiBadge, objectiveId: number): Badge {
    return {
      badgeId: b.badgeId,
      description: b.description,
      outcomeId: b.outcome.outcomeId,
      objectiveId
    };
  },

  convertApiBadgeWithObjective(b: ApiBadgeWithObjective): Badge {
    return this.convertApiBadge(b, b.objective.objectiveId);
  },

  convertNewPacket(p: Packet): ApiNewPacket {
    return {
      ...p,
      outcomeSpace: {
        name: `${p.name} outcome space`,
        definedBy: p.outcomes.map(outcome => ({
          ...this.convertOutcome(outcome),
          // Remove temp ids
          outcomeId: undefined
        }))
      },
      kind:
        p.kind === BasePacketKind.SINGLE_POINT
          ? 'single_point_rubric'
          : 'multi_point_rubric',
      // If this is a single point packet, use the packet name and description
      // for the objective too.
      objectives: p.objectives.map(objective => ({
        ...this.convertObjective(objective),
        // Remove temp ids
        objectiveId: undefined
      }))
    };
  }
};

/**
 * API Interfaces
 */
export interface ApiPacket {
  packetId: number;
  name: string;
  description: string;
  kind: string;
  organization: BaseOrganization;
  public: boolean;
  canEdit?: boolean;
  objectives: ApiObjective[];
  outcomeSpace: ApiOutcomeSpace;
  usage: {
    org?: number;
    orgs?: number;
  };
}

// The shape of the model used to create a packet.
// `objectives` is only populated for single rubric packets.
interface ApiNewPacket {
  name: string;
  description: string;
  kind: string;
  outcomeSpace: {
    name: string;
    definedBy: Omit<ApiOutcome, 'outcomeId'>[];
  };
  objectives: Omit<ApiObjective, 'objectiveId'>[];
}

interface ApiOutcomeSpace {
  name: string;
  definedBy: ApiOutcome[];
}

export interface ApiObjective {
  objectiveId: number;
  name: string;
  description: string;
  iconCodePoint: number;
  iconFontFamily: string;
  useOutcomeIcon: boolean;
  isActive: boolean;
}

export interface ApiOutcome {
  outcomeId: number;
  value: string;
  isActive: boolean;
  iconCodePoint: number;
  iconFontFamily: string;
  color: string;
  sortOrder: number;
}

export interface ApiBadge {
  badgeId: number;
  description: string;
  outcome: ApiOutcome;
}

export interface ApiBadgeWithObjective extends ApiBadge {
  objective: ApiObjective;
}
export interface Badge {
  badgeId: number;
  description: string;
  outcomeId: number;
  objectiveId: number;
}
